import React, { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { applyTheme } from './applyTheme';
import { applyBrowserBranding } from './browserBranding';
import { detectTenant } from './detectTenant';
import { loadManifest } from './loadManifest';
import { BrandManifest, DEFAULT_MANIFEST } from './manifest.schema';
import { resolvePublicAssetPath } from './publicAsset';
import { setApiBaseUrl, setApiBrandConfig } from '../services/api';
import { DEFAULT_SESSION_EXPIRED_MESSAGE, setSessionExpiredMessage } from './runtimeCopy';

const BRAND_BOOT_TIMEOUT_MS = 5000;

/** Replace {key} placeholders in template with values from params. */
function interpolate(template: string, params: Record<string, string>): string {
  return Object.entries(params).reduce(
    (acc, [key, value]) => acc.replace(new RegExp(`\\{${key}\\}`, 'g'), value),
    template
  );
}

type BrandContextValue = {
  manifest: BrandManifest;
  status: 'loading' | 'ready';
  asset: (key: string, fallback?: string) => string;
  t: (key: string, paramsOrFallback?: Record<string, string> | string, fallback?: string) => string;
  has: (feature: string) => boolean;
  apiBaseUrl?: string;
};

export const BrandContext = createContext<BrandContextValue>({
  manifest: DEFAULT_MANIFEST,
  status: 'ready',
  asset: (_key: string, fallback?: string) => fallback || '',
  t: (key: string, paramsOrFallback?: Record<string, string> | string, fallback?: string) =>
    (typeof paramsOrFallback === 'string' ? paramsOrFallback : fallback) ?? key,
  has: () => false,
  apiBaseUrl: undefined
});

export function BrandProvider({ children, onReady }: { children: React.ReactNode; onReady?: () => void }): JSX.Element {
  const [manifest, setManifest] = useState<BrandManifest>(DEFAULT_MANIFEST);
  const [status, setStatus] = useState<'loading' | 'ready'>('loading');
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    let mounted = true;

    function commitManifest(loaded: BrandManifest) {
      applyTheme(loaded.theme.tokens);
      setApiBaseUrl(loaded.api?.baseUrl);
      setApiBrandConfig(
        loaded.api
          ? {
              headers: loaded.api.headers,
              tenantSlug: loaded.api.tenantSlug ?? loaded.api.tenantId
            }
          : null
      );
      setSessionExpiredMessage(loaded.copy?.['auth.sessionExpired'] ?? DEFAULT_SESSION_EXPIRED_MESSAGE);
      applyBrowserBranding(loaded);
      setManifest(loaded);
      setStatus('ready');
      onReadyRef.current?.();
    }

    async function run() {
      const tenantId = detectTenant();
      const loaded = await loadManifest(tenantId, {
        timeoutMs: BRAND_BOOT_TIMEOUT_MS,
        onFallback: ({ requestedTenantId, reason }) => {
          console.warn(`[branding] Falling back to default tenant branding for "${requestedTenantId}": ${reason}`);
        }
      });

      if (!mounted) {
        return;
      }

      commitManifest(loaded);
    }

    run().catch((error) => {
      if (!mounted) {
        return;
      }

      const reason = error instanceof Error ? error.message : 'Unknown branding bootstrap failure';
      console.warn(`[branding] Falling back to default tenant branding for "${detectTenant()}": ${reason}`);
      commitManifest(DEFAULT_MANIFEST);
    });

    return () => {
      mounted = false;
    };
  }, []);

  const value = useMemo<BrandContextValue>(() => {
    return {
      manifest,
      status,
      asset: (key: string, fallback?: string) => {
        const brandedAsset = manifest.assets?.[key as keyof BrandManifest['assets']];
        return brandedAsset ? resolvePublicAssetPath(brandedAsset) : fallback || '';
      },
      t: (key: string, paramsOrFallback?: Record<string, string> | string, fallback?: string) => {
        const raw = manifest.copy?.[key];
        const template = raw ?? (typeof paramsOrFallback === 'string' ? paramsOrFallback : fallback) ?? key;
        if (typeof paramsOrFallback === 'object' && paramsOrFallback !== null) {
          return interpolate(template, paramsOrFallback);
        }
        return template;
      },
      has: (feature: string) => Boolean(manifest.features?.[feature]),
      apiBaseUrl: manifest.api?.baseUrl
    };
  }, [manifest, status]);

  return (
    <BrandContext.Provider value={value}>
      {status === 'ready' ? children : (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '3px solid #e0e0e0',
              borderTop: '3px solid #2196f3',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}
          />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
    </BrandContext.Provider>
  );
}
